import { Visibility } from "../../gql-generated/types";
import { type LensPreviewMediaItem } from "../../shared/state";

/**
 * Validation result for Exclusive Lens requirements
 */
export interface ExclusiveLensValidation {
    isValid: boolean;
    errorMessage?: string;
}

/**
 * Parameters for validating Exclusive Lens requirements
 */
export interface ValidateExclusiveLensParams {
    hasMusic?: boolean;
    hasLyrics?: boolean;
    previewMediaItems: readonly LensPreviewMediaItem[];
    visibility?: Visibility;
}

/**
 * Validation errors for exclusive lens requirements
 */
const VALIDATION_ERRORS = {
    NO_VIDEO: "Lens+ Payouts Lenses requires a video preview",
    HAS_MUSIC: "Lens+ Payouts Lenses cannot contain embedded music. Please remove music in Lens Studio and resubmit",
    HAS_LYRICS: "Lens+ Payouts Lenses cannot contain lyrics. Please remove lyrics in Lens Studio and resubmit",
    NOT_PUBLIC: "Lens+ Payouts Lenses must have public visibility",
} as const;

/**
 * Check if the first preview media item is a valid video
 */
const hasValidVideoPreview = (previewMediaItems: readonly LensPreviewMediaItem[]): boolean => {
    const firstItem = previewMediaItems[0];
    return Boolean(firstItem?.isVideo) && firstItem?.type !== "default";
};

/**
 * Validate Exclusive Lens requirements:
 * - Must have video preview
 * - Must not contain embedded music or lyrics
 * - Must have public visibility
 *
 * @param params - Validation parameters
 * @returns Validation result with isValid flag and optional error message
 */
export const validateExclusiveLens = ({
    hasMusic,
    hasLyrics,
    previewMediaItems,
    visibility,
}: ValidateExclusiveLensParams): ExclusiveLensValidation => {
    const hasVideo = hasValidVideoPreview(previewMediaItems);
    const isPublic = visibility === Visibility.VisibilityPublic;

    // Early return for valid case
    if (hasVideo && isPublic && !hasMusic && !hasLyrics) {
        return { isValid: true };
    }

    // Collect all validation errors
    const errors: string[] = [];

    if (!hasVideo) {
        errors.push(VALIDATION_ERRORS.NO_VIDEO);
    }

    if (hasMusic) {
        errors.push(VALIDATION_ERRORS.HAS_MUSIC);
    }

    if (hasLyrics) {
        errors.push(VALIDATION_ERRORS.HAS_LYRICS);
    }

    if (!isPublic) {
        errors.push(VALIDATION_ERRORS.NOT_PUBLIC);
    }

    return {
        isValid: false,
        errorMessage: errors.join(". "),
    };
};
